import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, Building, DoorOpen, Wrench, User, Notepad,
  ClipboardText, Package, X, ArrowElbowDownLeft,
  Plus, WarningOctagon, Lightning, Broadcast, Clock,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Category → icon + label metadata (order = display order in results)
const CATEGORIES = [
  { key: "actions",     label: "Actions",     Icon: Lightning },
  { key: "recent",      label: "Recent",      Icon: Clock },
  { key: "portfolios",  label: "Portfolios",  Icon: Building },
  { key: "units",       label: "Units",       Icon: DoorOpen },
  { key: "jobs",        label: "Work Orders", Icon: Wrench },
  { key: "landlords",   label: "Landlords",   Icon: User },
  { key: "projects",    label: "Projects",    Icon: Notepad },
  { key: "inspections", label: "Inspections", Icon: ClipboardText },
  { key: "assets",      label: "Assets",      Icon: Package },
];

// Static action items — always available
const ACTIONS = [
  { id: "act-new-job",    label: "Create Work Order",   sub: "Full new-job form", path: "/admin/jobs?new=1",           Icon: Plus,             kw: "new job create work order wo" },
  { id: "act-quick-job",  label: "Quick Job",           sub: "3-tap AI-drafted WO", path: "/admin/quick-job",           Icon: Lightning,        kw: "quick job fast" },
  { id: "act-log-eta",    label: "Log ETA Request",     sub: "Habitability tracking", path: "/admin/eta",                 Icon: WarningOctagon,   kw: "eta log habitability" },
  { id: "act-portal",     label: "Mint Portal Link",    sub: "Tenant portal invite", path: "/admin/settings/tenant-portal", Icon: Broadcast,        kw: "portal tenant mint invite" },
  { id: "act-chew-hub",   label: "Chew Hub",            sub: "Student Portal Control Tower", path: "/admin/chew-hub",          Icon: Building,         kw: "chew hub students unit" },
  { id: "act-sms",        label: "SMS Photos Settings", sub: "Twilio config + whitelist", path: "/admin/settings/sms-photos",  Icon: Broadcast,        kw: "sms twilio phone whitelist" },
  { id: "act-csv-history",label: "CSV Sync History",    sub: "AppFolio import runs + counts", path: "/admin/csv-sync-history",   Icon: Clock,            kw: "csv history sync appfolio import" },
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const kbdSymbol = isMac ? "⌘" : "Ctrl";
const RECENT_KEY = "fr:cmdk:recent";

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").slice(0, 5); } catch { return []; }
}
function pushRecent(item) {
  try {
    const cur = loadRecent().filter(r => !(r.id === item.id && r._cat === item._cat));
    const next = [{ id: item.id, _cat: item._cat, label: item.label, sub: item.sub, path: item.path }, ...cur].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

export default function CommandPalette() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  // Global keyboard shortcut ⌘K / Ctrl+K + Esc
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, open]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(trimmed)}`)
        .then((r) => setResults(r.data.results || {}))
        .catch(() => setResults({}))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  // Actions + recent overlay — merged into results on every render
  const mergedResults = useMemo(() => {
    const trimmed = q.trim().toLowerCase();
    // Actions matching keywords (or all actions if query is empty)
    const actions = trimmed.length === 0
      ? ACTIONS.slice(0, 6)
      : ACTIONS.filter(a => a.kw.includes(trimmed) || a.label.toLowerCase().includes(trimmed));
    const recent = trimmed.length === 0 ? loadRecent() : [];
    const base = results || {};
    return { ...base, actions, recent };
  }, [q, results]);

  // Flatten for keyboard nav
  const flat = useMemo(() => {
    const out = [];
    for (const c of CATEGORIES) {
      const items = mergedResults[c.key] || [];
      items.forEach((it) => out.push({ ...it, _cat: c.key, _catLabel: c.label, _Icon: it.Icon || c.Icon }));
    }
    return out;
  }, [mergedResults]);

  useEffect(() => { setHighlight(0); }, [flat.length]);

  const go = (item) => {
    setOpen(false);
    if (item?.path) {
      pushRecent(item);
      nav(item.path);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(flat.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[highlight];
      if (item) go(item);
    }
  };

  // Don't render for logged-out users
  if (!user) return null;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:pt-[10vh] bg-slate-950/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="cmdk-overlay"
        >
          <div
            className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
          >
            {/* Input */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
              <MagnifyingGlass weight="bold" size={16} className="text-slate-400"/>
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Jump to a unit, WO, resident, asset…"
                className="flex-1 bg-transparent text-sm placeholder:text-slate-400 focus:outline-none"
                data-testid="cmdk-input"
              />
              {loading && <span className="text-[10px] text-slate-400 font-mono">searching…</span>}
              <button
                onClick={() => setOpen(false)}
                data-testid="cmdk-close"
                className="text-slate-400 hover:text-slate-800 p-1 rounded hover:bg-slate-100"
                aria-label="Close"
              >
                <X weight="bold" size={14}/>
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto" data-testid="cmdk-results">
              {!results && q.trim().length < 2 && flat.length === 0 && (
                <div className="p-8 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-cyan-50 border border-cyan-500 flex items-center justify-center mb-2">
                    <MagnifyingGlass weight="bold" size={20} className="text-cyan-600"/>
                  </div>
                  <div className="text-sm font-black text-slate-900">Field Radar Quick Jump</div>
                  <div className="text-[12px] text-slate-500 mt-1">
                    Type at least 2 characters to search across your entire portfolio.
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5 justify-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <span>Unit #</span><span>·</span><span>WO#</span><span>·</span><span>Address</span><span>·</span><span>Resident</span><span>·</span><span>Serial</span>
                  </div>
                </div>
              )}

              {q.trim().length >= 2 && results && flat.length === 0 && !loading && (
                <div className="p-8 text-center text-slate-400 text-sm">
                  No matches for <span className="font-black text-slate-700">"{q}"</span>
                </div>
              )}
              {q.trim().length >= 3 && (
                <button onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("proman:ask", { detail: { message: q.trim() } })); }}
                  data-testid="palette-ask-radar"
                  className="w-full text-left px-3 py-3 flex items-center gap-3 bg-[#0f172a] text-white hover:bg-[#00C4CC] transition-colors">
                  <span className="h-8 w-8 flex items-center justify-center bg-[#00C4CC] text-white font-black text-xs">AI</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-black truncate">Ask Radar: "{q.trim()}"</span>
                    <span className="block text-[10px] font-bold uppercase tracking-widest opacity-70">How-to, where is…, or do it for me</span>
                  </span>
                </button>
              )}

              {CATEGORIES.map((c) => {
                const items = mergedResults[c.key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={c.key}>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50">
                      {c.label} · {items.length}
                    </div>
                    {items.map((it, i) => {
                      const flatIdx = flat.findIndex((f) => f._cat === c.key && f.id === it.id);
                      const active = flatIdx === highlight;
                      const ItemIcon = it.Icon || c.Icon;
                      return (
                        <button
                          key={`${c.key}-${it.id}-${i}`}
                          onClick={() => go({ ...it, _cat: c.key })}
                          onMouseEnter={() => setHighlight(flatIdx)}
                          data-testid={`cmdk-item-${c.key}-${it.id}`}
                          className={`w-full text-left flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 ${active ? "bg-cyan-50" : "hover:bg-slate-50"}`}
                        >
                          <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${active ? "bg-cyan-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                            <ItemIcon weight="bold" size={14}/>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-bold text-slate-900 truncate">{it.label}</div>
                            {it.sub && <div className="text-[11px] text-slate-500 truncate">{it.sub}</div>}
                          </div>
                          {active && <ArrowElbowDownLeft weight="bold" size={12} className="text-cyan-600 flex-shrink-0"/>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 flex items-center gap-3 text-[10px] text-slate-500 font-mono">
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded text-[9px]">↑↓</kbd> navigate</span>
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded text-[9px]">↵</kbd> open</span>
              <span><kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded text-[9px]">Esc</kbd> close</span>
              <span className="ml-auto text-slate-400">Powered by /api/search</span>
            </div>
          </div>
        </div>
      )}

      {/* Floating hint chip (bottom-left corner) — only shows on desktop, first-time visitors */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="cmdk-hint"
          className="fixed bottom-4 left-4 z-40 hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-900/90 hover:bg-slate-800 text-slate-200 text-[11px] font-bold shadow-lg border border-slate-700 backdrop-blur"
          title={`Quick Jump (${kbdSymbol} + K)`}
        >
          <MagnifyingGlass weight="bold" size={12}/>
          <span>Quick Jump</span>
          <kbd className="ml-1 px-1 py-0.5 bg-slate-800 border border-slate-600 rounded text-[9px] font-mono">{kbdSymbol}K</kbd>
        </button>
      )}
    </>
  );
}
